import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Plus, X, CheckCircle, Circle, DotsSixVertical, Info, Sparkle } from "@phosphor-icons/react";
import { DAY_LABELS } from "../../hooks/usePlanBuilder";
import { FOCUS_OPTIONS, FOCUS_BLUEPRINTS, buildExercisesForFocus, focusToLegacyLabel, LEGACY_FOCUS_MAP } from "../../lib/focusBlueprints";
import { autoAssignSection } from "../../lib/sections";

const SECTIONS = [
  { id: "warmup", short: "WU", label: "WARM-UP", color: "text-orange-400", border: "border-orange-400/40" },
  { id: "workout", short: "WO", label: "WORKOUT", color: "text-volt", border: "border-volt/40" },
  { id: "cooldown", short: "CD", label: "COOL-DOWN", color: "text-sky-400", border: "border-sky-400/40" },
];

const SECTION_BY_ID = Object.fromEntries(SECTIONS.map((s) => [s.id, s]));

function SortableExerciseItem({ id, ex, exIdx, def, onChange, onRemove, onInfo, wi, di }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  const domain = def?.domain || "Strength";

  // Endurance / ACFT cardio gets a dedicated 4-input row.
  // Mobility uses a simpler 2-field layout.
  // Everything else uses the classic strength Sets / Reps / Wt layout.
  const layout =
    domain === "Endurance" ? "endurance" :
    domain === "Mobility" ? "mobility" :
    "strength";

  const sectionId = ex.section || "workout";
  const cycleSection = (e) => {
    e.stopPropagation();
    const idx = SECTIONS.findIndex((s) => s.id === sectionId);
    const next = SECTIONS[(idx + 1) % SECTIONS.length];
    onChange(exIdx, { section: next.id });
  };

  return (
    <div ref={setNodeRef} style={style} className="bg-card border border-border p-2 text-xs" data-testid={`ex-item-${wi}-${di}-${exIdx}`}>
      <div className="flex justify-between items-start gap-2">
        <button {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground touch-none" data-testid={`drag-handle-${wi}-${di}-${exIdx}`}>
          <DotsSixVertical size={14} weight="bold" />
        </button>
        <button
          onClick={() => onInfo && onInfo(ex.exercise_id, wi, di, exIdx)}
          className="font-display text-sm font-bold uppercase truncate flex-1 text-left hover:text-volt inline-flex items-center gap-1"
          data-testid={`ex-info-btn-${wi}-${di}-${exIdx}`}
          title="View example & instructions"
        >
          <Info size={12} weight="bold" className="shrink-0 text-muted-foreground" />
          <span className="truncate">{def?.name || ex.exercise_id}</span>
        </button>
        <button onClick={() => onRemove(exIdx)} className="text-muted-foreground hover:text-destructive shrink-0" data-testid={`remove-ex-${wi}-${di}-${exIdx}`}>
          <X size={12} weight="bold" />
        </button>
      </div>

      {layout === "endurance" && (
        <div className="grid grid-cols-4 gap-1 mt-1">
          <input value={ex.sets || ""} onChange={(e) => onChange(exIdx, { sets: parseInt(e.target.value) || null })}
            placeholder="Rnds" title="Rounds / intervals"
            className="bg-secondary border border-border px-1 py-0.5 min-w-0 w-full" />
          <input value={ex.reps || ""} onChange={(e) => onChange(exIdx, { reps: e.target.value })}
            placeholder="Dist" title="Distance or duration"
            className="bg-secondary border border-border px-1 py-0.5 min-w-0 w-full" />
          <input value={ex.weight || ""} onChange={(e) => onChange(exIdx, { weight: e.target.value })}
            data-testid={`weight-${wi}-${di}-${exIdx}`}
            placeholder="Load" title="External load"
            className="bg-secondary border border-border px-1 py-0.5 min-w-0 w-full" />
          <input value={ex.notes || ""} onChange={(e) => onChange(exIdx, { notes: e.target.value })}
            placeholder="Pace" title="Pace or coaching note"
            className="bg-secondary border border-border px-1 py-0.5 min-w-0 w-full" />
        </div>
      )}

      {layout === "mobility" && (
        <div className="grid grid-cols-3 gap-1 mt-1">
          <input value={ex.sets || ""} onChange={(e) => onChange(exIdx, { sets: parseInt(e.target.value) || null })}
            placeholder="Rnds" title="Rounds"
            className="bg-secondary border border-border px-1 py-0.5 min-w-0 w-full" />
          <input value={ex.reps || ""} onChange={(e) => onChange(exIdx, { reps: e.target.value })}
            placeholder="Hold" title="Hold time or reps"
            className="bg-secondary border border-border px-1 py-0.5 min-w-0 w-full" />
          <input value={ex.notes || ""} onChange={(e) => onChange(exIdx, { notes: e.target.value })}
            placeholder="Note" title="Coaching cue"
            className="bg-secondary border border-border px-1 py-0.5 min-w-0 w-full" />
        </div>
      )}

      {layout === "strength" && (
        <div className="grid grid-cols-3 gap-1 mt-1">
          <input value={ex.sets || ""} onChange={(e) => onChange(exIdx, { sets: parseInt(e.target.value) || null })}
            placeholder="Sets" title="Sets"
            className="bg-secondary border border-border px-1 py-0.5 min-w-0 w-full" />
          <input value={ex.reps || ""} onChange={(e) => onChange(exIdx, { reps: e.target.value })}
            placeholder="Reps" title="Reps or rep range"
            className="bg-secondary border border-border px-1 py-0.5 min-w-0 w-full" />
          <input value={ex.weight || ""} onChange={(e) => onChange(exIdx, { weight: e.target.value })}
            data-testid={`weight-${wi}-${di}-${exIdx}`}
            placeholder="Wt" title="Weight"
            className="bg-secondary border border-border px-1 py-0.5 min-w-0 w-full" />
        </div>
      )}

      {/* RPE — universal */}
      <div className="mt-1 relative">
        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] uppercase tracking-widest text-muted-foreground pointer-events-none" aria-hidden="true">RPE</span>
        <input
          value={ex.rpe || ""}
          onChange={(e) => onChange(exIdx, { rpe: e.target.value })}
          placeholder="@7"
          title="Rate of Perceived Exertion"
          className="bg-secondary border border-border w-full pl-10 pr-2 py-0.5 min-w-0"
          data-testid={`rpe-${wi}-${di}-${exIdx}`}
          inputMode="decimal"
        />
      </div>

      <div className="mt-1 flex items-center gap-1">
        <button
          onClick={cycleSection}
          className={`px-1.5 py-0.5 text-[9px] font-display font-black tracking-widest border ${SECTION_BY_ID[sectionId].color} ${SECTION_BY_ID[sectionId].border} hover:bg-secondary`}
          data-testid={`ex-section-${wi}-${di}-${exIdx}`}
          title={`Section: ${SECTION_BY_ID[sectionId].label}. Click to move.`}
        >
          {SECTION_BY_ID[sectionId].short}
        </button>
        {layout !== "endurance" && ex.notes && (
          <div className="text-[10px] text-volt truncate flex-1" title={ex.notes}>{ex.notes}</div>
        )}
      </div>
    </div>
  );
}

function SortableExerciseList({ items, indices, onReorder, onChange, onRemove, onInfo, exMap, wi, di }) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));
  const ids = indices.map((idx) => `${wi}-${di}-${idx}`);
  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={({ active, over }) => {
      if (!over || active.id === over.id) return;
      // Active/over are local-to-section IDs. Map them back to absolute indices.
      const fromLocal = ids.indexOf(active.id);
      const toLocal = ids.indexOf(over.id);
      if (fromLocal === -1 || toLocal === -1) return;
      const fromAbs = indices[fromLocal];
      const toAbs = indices[toLocal];
      onReorder(fromAbs, toAbs);
    }}>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <div className="space-y-2">
          {indices.map((absIdx, localIdx) => (
            <SortableExerciseItem
              key={ids[localIdx]}
              id={ids[localIdx]}
              ex={items[absIdx]}
              exIdx={absIdx}
              def={exMap[items[absIdx].exercise_id]}
              onChange={onChange}
              onRemove={onRemove}
              onInfo={onInfo}
              wi={wi}
              di={di}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

export default function DayCard({ wi, di, day, isComplete, exMap, onToggleComplete, onUpdateDay, onReorder, onChangeExercise, onRemoveExercise, onOpenPicker, onShowExerciseInfo, onReplaceExercises, assessmentNotes }) {
  // Bucket exercises by their section (default workout). Track original
  // indices so reorder, change, and remove handlers still address the
  // canonical flat list.
  const buckets = { warmup: [], workout: [], cooldown: [] };
  (day.exercises || []).forEach((ex, idx) => {
    const key = buckets[ex.section] ? ex.section : "workout";
    buckets[key].push(idx);
  });

  const suggestWorkout = () => {
    const preferred = day.structure_focus_id
      || LEGACY_FOCUS_MAP[day.focus]
      || (day.focus === "Strength" ? "FullBody" : null);
    const focusId = preferred && FOCUS_BLUEPRINTS[preferred] ? preferred : "FullBody";
    const items = buildExercisesForFocus(focusId, exMap, assessmentNotes);
    if (items.length === 0) return;
    if (day.exercises?.length > 0 && !window.confirm(`Replace the ${day.exercises.length} current exercises with a suggested ${focusId} workout?`)) return;
    // Stamp a section on every suggested exercise based on its kind.
    const withSections = items.map((it) => ({ ...it, section: autoAssignSection(it.exercise_id, exMap[it.exercise_id]) }));
    onReplaceExercises?.(wi, di, withSections, { focus: focusToLegacyLabel(focusId), structure_focus_id: focusId });
  };

  return (
    <div className={`bg-background p-3 min-h-[200px] flex flex-col ${isComplete ? "ring-1 ring-volt/40" : ""}`}>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <button onClick={() => onToggleComplete(wi, di)} title="Mark day complete" className={`shrink-0 ${isComplete ? "text-volt" : "text-muted-foreground hover:text-foreground"}`} data-testid={`complete-${wi}-${di}`}>
          {isComplete ? <CheckCircle size={20} weight="fill" /> : <Circle size={20} weight="bold" />}
        </button>
        <div className="font-display text-lg font-black text-volt shrink-0">{DAY_LABELS[di]}</div>
        <select
          value={day.structure_focus_id || LEGACY_FOCUS_MAP[day.focus] || "FullBody"}
          onChange={(e) => {
            const focusId = e.target.value;
            onUpdateDay(wi, di, {
              focus: focusToLegacyLabel(focusId),
              structure_focus_id: focusId,
              label: `${DAY_LABELS[di]} - ${FOCUS_OPTIONS.find((o) => o.id === focusId)?.short || focusId}`,
            });
          }}
          className="bg-secondary border border-border px-2 py-1 text-[10px] uppercase tracking-wider ml-auto max-w-[140px] truncate min-w-0"
          data-testid={`focus-${wi}-${di}`}
        >
          {FOCUS_OPTIONS.map((f) => <option key={f.id} value={f.id}>{f.short}</option>)}
        </select>
      </div>

      {/* Three section blocks. Each has its own sortable list so drag
          stays inside a section; user moves between sections via the
          WU/WO/CD pill on each item. */}
      <div className="space-y-3 flex-1">
        {SECTIONS.map((s) => (
          <div key={s.id} data-testid={`section-${s.id}-${wi}-${di}`}>
            <div className={`label-mono text-[10px] mb-1 ${s.color}`}>// {s.label}</div>
            {buckets[s.id].length === 0 ? (
              <div className="text-[10px] text-muted-foreground/60 italic pl-1">— empty —</div>
            ) : (
              <SortableExerciseList
                items={day.exercises}
                indices={buckets[s.id]}
                onReorder={(oldIdx, newIdx) => onReorder(wi, di, oldIdx, newIdx)}
                onChange={(exIdx, patch) => onChangeExercise(wi, di, exIdx, patch)}
                onRemove={(exIdx) => onRemoveExercise(wi, di, exIdx)}
                onInfo={onShowExerciseInfo}
                exMap={exMap}
                wi={wi}
                di={di}
              />
            )}
          </div>
        ))}
      </div>

      <div className="mt-2 grid grid-cols-2 gap-1">
        <button onClick={() => onOpenPicker(wi, di)} className="border border-dashed border-border p-2 text-xs uppercase tracking-widest text-muted-foreground hover:border-volt hover:text-volt flex items-center justify-center gap-1" data-testid={`add-ex-btn-${wi}-${di}`}>
          <Plus size={12} weight="bold" /> Add
        </button>
        <button
          onClick={suggestWorkout}
          className="border border-dashed border-volt/40 text-volt/80 p-2 text-xs uppercase tracking-widest hover:border-volt hover:bg-volt/10 flex items-center justify-center gap-1"
          data-testid={`suggest-btn-${wi}-${di}`}
          title="Auto-fill a suggested workout based on this day's focus"
        >
          <Sparkle size={12} weight="bold" /> Suggest
        </button>
      </div>
    </div>
  );
}
